/** Console v0.5: Home (design brief v0.5, the Main mock-up), on session A's GET /api/home (#375, contracts s.12)
 * and today's meetings list. Everything here is the caller's own scope, worked out by the server. The Ask / Delegate
 * box has no backend yet (Prabash's decision pending), so it says so rather than pretending. */

import { ApiError, fetchMeetings } from "../api.js";
import type { MeetingRow } from "../api.js";
import type { ScreenContext } from "./app.js";
import { SCREENS } from "./app.js";
import { AI_PROMISE, SERVICES } from "./copy.js";
import { dueLabel, greeting, longToday, plural, shortDay } from "./format.js";
import { fetchHome } from "./items-api.js";
import type { Home, ItemRow } from "./items-api.js";
import { routePath } from "./routes.js";
import { shortDate } from "./team-logic.js";
import { card, chip, figure, h, legalFooter, loading, notice, progress, sectionCard, statTile, tabs, uid } from "./ui.js";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** Ask / Delegate (Main mock-up): shown as the design has it, but sending waits for a backend (Prabash's decision). */
function askBox(): HTMLElement {
  let mode: "ask" | "delegate" = "ask";
  const wrap = h("div", "pk5-ask");
  const draw = () => {
    const panelId = uid("pk5-ask-panel");
    const input = h("textarea", "pk5-ask-input");
    input.rows = 3;
    const inputId = uid("pk5-ask-input");
    input.id = inputId;
    input.placeholder =
      mode === "ask" ? "Ask about any figure, deadline or piece of work" : "Type @ to choose an agent, e.g. @BAS prepare Q1 for an organisation";
    input.disabled = true;
    const label = h("label", "pk-visually-hidden", mode === "ask" ? "Ask Preekie" : "Delegate work to an agent");
    label.htmlFor = inputId;
    const soon = h("p", "pk5-ask-soon", mode === "ask" ? "Asking Preekie is coming soon." : "Delegating to an agent is coming soon.");
    const panel = h("div", "pk5-ask-panel", label, input, soon);
    panel.id = panelId;
    panel.setAttribute("role", "tabpanel");
    wrap.replaceChildren(
      tabs(
        "Mode",
        [
          { id: "ask", label: "Ask" },
          { id: "delegate", label: "Delegate to an agent" },
        ],
        mode,
        (m) => {
          mode = m;
          draw();
        },
        panelId
      ),
      panel,
      h("p", "pk5-ask-promise", AI_PROMISE)
    );
  };
  draw();
  return wrap;
}

function queueRow(item: ItemRow, practice: boolean, ctx: ScreenContext): HTMLElement {
  const a = h(
    "a",
    "pk5-queue-row",
    h(
      "span",
      "pk5-queue-text",
      h("span", "pk5-queue-task", item.task),
      h("span", "pk5-muted", practice ? `${item.organisation_name} · prepared by ${item.agent}` : `Prepared by ${item.agent}`)
    ),
    chip(item.status_label),
    figure(dueLabel(item.due, new Date(), ctx.timeZone))
  );
  a.href = `${routePath({ kind: "inbox" })}?item=${encodeURIComponent(item.item_id)}`;
  return a;
}

function home(ctx: ScreenContext): () => void {
  const { me } = ctx;
  const practice = me.account?.type !== "company";
  let data: Home | null = null;
  let error: string | null = null;
  let meetings: MeetingRow[] | null = null;
  let gone = false;
  const ask = askBox(); // kept across redraws, so the chosen tab survives loading

  const draw = () => {
    if (gone) return;
    const now = new Date();
    const intro = h("section", "pk5-home-intro");
    intro.setAttribute("aria-labelledby", "pk5-title");
    const h1 = h("h1", "pk5-title", greeting(now, ctx.timeZone, data?.greeting_name ?? me.display_name ?? null));
    h1.id = "pk5-title";
    h1.tabIndex = -1;
    const need = data ? data.tiles.awaiting_review.count + data.tiles.exceptions.count : null;
    intro.append(
      h("p", "pk5-eyebrow", longToday(now, ctx.timeZone)),
      h1,
      h(
        "p",
        "pk5-intro",
        need === null ? "" : need === 0 ? "Nothing needs your attention right now." : `${plural(need, "item needs", "items need")} your attention.`
      ),
      ask
    );
    const parts: Node[] = [intro];
    if (error) parts.push(notice(error, "alert"));
    else if (!data) parts.push(loading());
    else {
      const t = data.tiles;
      const tiles = h(
        "section",
        "pk5-stats",
        statTile("Awaiting review", String(t.awaiting_review.count), t.awaiting_review.due_today ? `${t.awaiting_review.due_today} due today` : "None due today", "/inbox"),
        statTile("Exceptions", String(t.exceptions.count), t.exceptions.count ? "Needs your judgement" : "None right now", "/inbox?tab=exceptions"),
        statTile(
          "Lodgements due this week",
          String(t.lodgements_due_this_week.count),
          t.lodgements_due_this_week.next_due ? h("span", null, "Next: ", figure(shortDay(t.lodgements_due_this_week.next_due))) : "None this week",
          "/inbox"
        ),
        ...(t.team ? [statTile("Team", String(t.team.people), t.team.pending_invites ? plural(t.team.pending_invites, "invite pending", "invites pending") : "No invites pending", "/team")] : [])
      );
      tiles.setAttribute("aria-label", "Today at a glance");
      parts.push(tiles);

      const link = h("a", "pk5-link", practice ? "All organisations" : "Open your organisation");
      link.href = "/organisations";
      const grid = h("div", "pk5-service-grid");
      for (const def of SERVICES) {
        const s = data.services.find((x) => x.service === def.service) ?? { open: 0, awaiting_you: 0 };
        const cardEl = h(
          "article",
          "pk5-service",
          h("div", "pk5-service-head", h("h3", "pk5-service-name", def.name), h("span", "pk5-muted", def.tag)),
          h("div", "pk5-service-big", figure(String(s.open)), h("span", "pk5-muted", s.open === 1 ? "open item" : "open items")),
          h("p", "pk5-service-line", s.awaiting_you ? `${s.awaiting_you} awaiting you` : "Nothing awaiting you"),
          def.note ? h("p", "pk5-service-note", def.note) : null,
          def.smallPrint ? h("p", "pk5-small-print", def.smallPrint) : null
        );
        grid.append(cardEl);
      }
      parts.push(sectionCard(practice ? "Across your organisations" : "Your business", [grid], link));

      const inboxLink = h("a", "pk5-link", "Open review inbox");
      inboxLink.href = "/inbox";
      const queue = data.queue.length
        ? h("div", "pk5-queue", ...data.queue.map((q) => queueRow(q, practice, ctx)))
        : h("p", "pk5-muted", "Nothing waiting for your sign-off.");
      const left = sectionCard("Your queue", [queue], inboxLink);

      const groups = data.month_end.groups;
      const close = sectionCard(data.month_end.label, groups.length ? groups.map((g) => progress(g.done, g.total, g.label)) : [h("p", "pk5-muted", "No month-end steps yet.")]);
      const right = h("div", "pk5-home-side", close);
      if (me.meetings_enabled === true) {
        const all = h("a", "pk5-link", "All meetings");
        all.href = "/meetings";
        const list: Node[] = meetings === null ? [loading()] : meetings.length ? meetings.slice(0, 3).map(meetingRow) : [h("p", "pk5-muted", "No meetings to review.")];
        right.append(sectionCard("Meetings", list, all));
      }
      parts.push(h("div", "pk5-home-split", left, right));
    }
    parts.push(legalFooter());
    ctx.root.replaceChildren(...parts);
  };

  const meetingRow = (m: MeetingRow): HTMLElement => {
    const a = h(
      "a",
      "pk5-meeting-row",
      figure(shortDate(m.scheduled_start, ctx.timeZone)),
      h("span", "pk5-queue-text", h("span", "pk5-queue-task", `${m.title} · ${m.client_name}`), h("span", "pk5-muted", m.processing ? "Preparing notes" : m.status))
    );
    a.href = `/meetings/${m.meeting_id}`;
    return a;
  };

  draw();
  void fetchHome()
    .then((d) => {
      data = d;
    })
    .catch((err: unknown) => {
      error = messageOf(err, "Preekie couldn't load Home. Try again in a few minutes.");
    })
    .then(draw);
  if (me.meetings_enabled === true) {
    void fetchMeetings([], false)
      .then((m) => {
        meetings = [...m.to_review, ...m.upcoming];
      })
      .catch(() => {
        meetings = [];
      })
      .then(draw);
  }
  return () => {
    gone = true;
  };
}

SCREENS.home = home;
